/**
 * RateBandsEditorTab — Admin > Rate bands (rates administration;
 * founder feedback 2026-07-10: the band vocabulary "should be a tenant
 * config setting btw and not hardcoded" — it already was config-driven
 * (#456 readRateBands); this makes it VISIBLE and EDITABLE).
 *
 * Shows the tenant's EFFECTIVE bands labeled with their source: the
 * platform default set until the first save materializes the tenant's
 * own config row (tenants.config features.rate_bands — nothing is
 * seeded). The owner (manage_team — the strictest existing
 * admin-config gate; the rail hides this view without it, like Limits
 * & access) can rename labels, reorder, and add bands freely; VALUES
 * are load-bearing tokens (client_rates.rate_band /
 * billable_events.rate_label), so a new band's value locks after its
 * first save and removing an in-use band comes back as a fact-shaped
 * 409 listing where it's used.
 *
 * A save invalidates ['tenant-config'] (every readRateBands consumer:
 * the Rates card, the composer, PreBilling) and the 0134 by-role
 * matrix query — both re-read the new vocabulary.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { FLD } from '@/components/primitives/modalFormStyles';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { contactSentence, saveFailureText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  fetchRateBandsSettings,
  isValidBandValue,
  putBandSwitch,
  putRateBands,
  rateBandInUseFacts,
  rateBandsSettingsKey,
  slugifyBandLabel,
  type RateBandWire,
  type RateBandsSettingsResponse,
} from '@/lib/rateBandsAdmin';

/** One editable row: existing bands carry a locked value; a band added
 *  in this session keeps its value editable until the save. */
interface EditableBand {
  /** Stable render identity — NOT the value (a new band's value follows
   *  its name while typing; keying on it would remount the input mid-
   *  keystroke and drop focus). */
  uid: string;
  value: string;
  label: string;
  /** true = the value token is already saved substrate — locked. */
  locked: boolean;
  /** true = the operator edited the token directly; stop auto-deriving
   *  it from the name. */
  valueTouched?: boolean;
}

function toEditable(bands: RateBandWire[]): EditableBand[] {
  return bands.map((b) => ({ uid: `saved-${b.value}`, value: b.value, label: b.label, locked: true }));
}

let nextUid = 0;

/** The polite pre-flight problems (the server re-validates). */
function draftProblem(bands: EditableBand[]): string | null {
  if (bands.length === 0) return 'Keep at least one rate band, since every note needs one.';
  const seen = new Set<string>();
  for (const b of bands) {
    if (b.label.trim() === '') return 'Every band needs a name.';
    if (!isValidBandValue(b.value)) {
      return `"${b.label.trim() || b.value}" needs a short code. Use only lowercase letters, numbers and underscores, with no spaces.`;
    }
    if (seen.has(b.value)) return `Two bands use the code "${b.value}". Give one of them a different code.`;
    seen.add(b.value);
  }
  return null;
}

/** "a" / "a and b" / "a, b and c" - the refusal now lists up to three
 *  places, and joining every one with "and" reads as a stutter. */
function joinPlaces(parts: readonly string[]): string {
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]!}`;
}

/** A wire count, defensively. The body is parsed from an error response,
 *  so nothing about its shape is guaranteed (the readRateBands
 *  defensive-walk posture). */
function usageCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/** Imported history runs to thousands of rows, so these numbers are read,
 *  not glanced at: 1,204 not 1204. */
/** On first: the usual state. Off reads in the house terracotta. */
const SWITCH_OPTIONS: readonly [SegmentedOption<boolean>, SegmentedOption<boolean>] = [
  { value: true, label: 'On', suffix: 'on' },
  { value: false, label: 'Off', suffix: 'off', checkedLook: 'terracotta' },
];

function readable(n: number): string {
  return n.toLocaleString();
}

export interface RateBandsEditorTabProps {
  tenantSlug: string;
}

export function RateBandsEditorTab({ tenantSlug }: RateBandsEditorTabProps): ReactNode {
  const queryClient = useQueryClient();
  const query = useQuery<RateBandsSettingsResponse, Error>({
    queryKey: rateBandsSettingsKey(tenantSlug),
    queryFn: ({ signal }) => fetchRateBandsSettings(tenantSlug, signal),
    staleTime: 30_000,
  });

  // null = not editing yet (mirror the fetched list on first edit).
  const [draft, setDraft] = useState<EditableBand[] | null>(null);
  const [saveError, setSaveError] = useState<ReactNode | null>(null);
  const contact = useSupportContact();

  const bands: EditableBand[] = draft ?? (query.data ? toEditable(query.data.bands) : []);
  const bandsOff = new Set(query.data?.bands_off ?? []);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // One band on or off. The band and the state travel as the variables.
  const switchMut = useMutation<{ bands_off: string[] }, unknown, { band: string; on: boolean }>({
    mutationFn: ({ band, on }) => putBandSwitch(tenantSlug, band, on),
    onSuccess: () => {
      setSwitchError(null);
      // The pickers, the Rates card and the server-served lists re-read.
      void queryClient.invalidateQueries({ queryKey: rateBandsSettingsKey(tenantSlug) });
      void queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'band-matrix', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'client-rates', tenantSlug] });
    },
    onError: (err) =>
      setSwitchError(
        saveFailureText(err, contact) ??
          contactSentence(
            "That band didn't switch, and it's unchanged. Try again, and if it happens again, tell {contact_system}.",
            contact,
          ),
      ),
  });
  const dirty = draft !== null;
  const problem = dirty ? draftProblem(bands) : null;

  // The request is built where Save is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  const saveMut = useMutation<RateBandsSettingsResponse, unknown, Array<{ value: string; label: string }>>({
    mutationFn: (next) => putRateBands(tenantSlug, next),
    onSuccess: (settings) => {
      setDraft(null);
      setSaveError(null);
      queryClient.setQueryData(rateBandsSettingsKey(tenantSlug), settings);
      // Every band-list consumer re-reads: readRateBands surfaces
      // (Rates card, composer, PreBilling) ride tenant-config; the 0134
      // by-role matrix and the Client rates grid are served aggregates.
      void queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'band-matrix', tenantSlug] });
      void queryClient.invalidateQueries({ queryKey: ['admin-review', 'client-rates', tenantSlug] });
    },
    onError: (err) => {
      const facts = rateBandInUseFacts(err);
      if (facts && facts.length > 0) {
        setSaveError(
          <span data-testid="rate-bands-in-use">
            {facts.map((f) => {
              const rates = usageCount(f.client_rates_count);
              // ONE count over both of billable_events' token columns
              // (0165): imported rows carry the token in rate_band while
              // rate_label keeps the raw CareTree name, and a row that
              // matches in both is one row, not two.
              const events = usageCount(f.billable_events_count);
              // Frozen history: money already on an issued document.
              const lines = usageCount(f.invoice_line_items_count);
              // Frozen snapshots: after a void the immutable snapshot is
              // a token's ONLY carrier (the void deletes the live line
              // rows), so this can be the sole place the refusal names.
              const snaps = usageCount(f.invoice_snapshots_count);
              const named = Array.isArray(f.clients) ? f.clients : [];
              const mapKeys = Array.isArray(f.qbo_item_map_keys) ? f.qbo_item_map_keys : [];
              const places = [
                rates > 0
                  ? `${readable(rates)} client rate${rates === 1 ? '' : 's'}${named.length > 0 ? ` (${named.join(', ')}${rates > named.length ? ', …' : ''})` : ''}`
                  : null,
                events > 0
                  ? `${readable(events)} billed or billable entr${events === 1 ? 'y' : 'ies'}`
                  : null,
                lines > 0 ? `${readable(lines)} invoice line${lines === 1 ? '' : 's'}` : null,
                snaps > 0 ? `${readable(snaps)} frozen snapshot${snaps === 1 ? '' : 's'}` : null,
              ].filter((p): p is string => p !== null);
              return (
                <span key={f.value} className="block">
                  {places.length > 0 ? (
                    <>
                      &ldquo;{f.value}&rdquo; can&rsquo;t be removed: it&rsquo;s still used by{' '}
                      {joinPlaces(places)}.
                    </>
                  ) : (
                    <>&ldquo;{f.value}&rdquo; can&rsquo;t be removed: it&rsquo;s still in use.</>
                  )}
                  {mapKeys.length > 0 ? (
                    <>
                      {' '}
                      Removing it would also strand its QuickBooks item mapping:{' '}
                      {mapKeys.join(', ')}.
                    </>
                  ) : null}
                </span>
              );
            })}
          </span>,
        );
      } else {
        setSaveError(
          saveFailureText(err, contact) ??
            contactSentence(
              "The rate bands didn't save. Check each band and save again, and if it fails again, tell {contact_system}.",
              contact,
            ),
        );
      }
    },
  });

  const edit = (next: EditableBand[]): void => {
    setSaveError(null);
    setDraft(next);
  };
  const move = (i: number, delta: -1 | 1): void => {
    const j = i + delta;
    if (j < 0 || j >= bands.length) return;
    const next = [...bands];
    const [row] = next.splice(i, 1);
    next.splice(j, 0, row!);
    edit(next);
  };

  return (
    <div className="max-w-3xl" data-testid="rate-bands-editor-tab">
      <h2 className="font-display text-lg text-sage-800">Rate bands</h2>
      <p className="mt-1 text-sm text-ink-soft">
        The kinds of work this workspace bills time under. Names can change any time; the token
        next to each band lives in saved rates and billed entries, so it locks after the first
        save.
      </p>

      {query.isError && (
        <div className="mt-4">
          <ErrorBanner error={query.error} onRetry={() => void query.refetch()} what="the rate bands" />
        </div>
      )}
      {query.isPending && (
        <div className="mt-6 space-y-2">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.data && (
        <>
          <p className="mt-3 text-sm text-ink-soft" data-testid="rate-bands-source">
            {query.data.source === 'platform_default' && !dirty
              ? 'These are the standard bands. Saving makes them your own.'
              : dirty
                ? 'Unsaved changes.'
                : 'Your own list.'}
          </p>

          <p className="mt-2 text-sm text-ink-soft" data-testid="rate-bands-switch-help">
            Off stops a band being offered for new notes and hides it from every client&rsquo;s Rates. Notes, rates and invoices already on it keep it.
          </p>
          {switchError !== null && (
            <p role="alert" className="mt-2 text-xs text-pepper" data-testid="rate-bands-switch-error">
              {switchError}
            </p>
          )}
          <ul className="mt-4 flex flex-col gap-2" data-testid="rate-bands-list">
            {bands.map((band, i) => (
              <li
                key={band.uid}
                className="flex flex-wrap items-center gap-2 rounded-base border border-sage-800/[8%] bg-paper px-3 py-2"
                data-testid={`rate-bands-row-${i}`}
              >
                <div className="flex flex-col">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${band.label || band.value} up`}
                    data-testid={`rate-bands-up-${i}`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="min-h-0 px-1 py-0 text-xs leading-none"
                  >
                    ▲
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${band.label || band.value} down`}
                    data-testid={`rate-bands-down-${i}`}
                    disabled={i === bands.length - 1}
                    onClick={() => move(i, 1)}
                    className="min-h-0 px-1 py-0 text-xs leading-none"
                  >
                    ▼
                  </Button>
                </div>
                <input
                  type="text"
                  aria-label="Band name"
                  data-testid={`rate-bands-label-${i}`}
                  className={`${FLD} h-9 w-56 px-2 text-[13px]`}
                  value={band.label}
                  onChange={(e) =>
                    edit(
                      bands.map((b, k) => {
                        if (k !== i) return b;
                        // A new band's token follows its name until the
                        // operator edits the token directly.
                        const value =
                          !b.locked && !b.valueTouched ? slugifyBandLabel(e.target.value) : b.value;
                        return { ...b, label: e.target.value, value };
                      }),
                    )
                  }
                />
                {band.locked ? (
                  <code className="text-xs text-ink-soft" data-testid={`rate-bands-value-${i}`}>
                    {band.value}
                  </code>
                ) : (
                  <input
                    type="text"
                    aria-label="Band token"
                    data-testid={`rate-bands-value-input-${i}`}
                    className={`${FLD} h-9 w-56 px-2 font-mono text-[12px]`}
                    value={band.value}
                    onChange={(e) =>
                      edit(
                        bands.map((b, k) =>
                          k === i ? { ...b, value: e.target.value, valueTouched: true } : b,
                        ),
                      )
                    }
                  />
                )}
                {band.locked && (
                  // A saved band can be switched off: not offered for a new
                  // note, refused by the server, gone from the Rates card;
                  // every note and rate already on it keeps it.
                  <SegmentedToggle
                    options={SWITCH_OPTIONS}
                    value={!bandsOff.has(band.value)}
                    onChange={(on) => switchMut.mutate({ band: band.value, on })}
                    name={`${band.label || band.value} offered`}
                    testIdPrefix={`rate-band-switch-${band.value}`}
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rate-bands-remove-${i}`}
                  onClick={() => edit(bands.filter((_, k) => k !== i))}
                  className="ml-auto min-h-0 px-1.5 py-0 text-xs"
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>

          <div className="mt-3">
            <Button
              variant="ghost"
              size="sm"
              data-testid="rate-bands-add"
              onClick={() =>
                edit([...bands, { uid: `new-${nextUid++}`, value: '', label: '', locked: false }])
              }
            >
              Add a band
            </Button>
          </div>

          {bands.some((b) => !b.locked) && (
            <p className="mt-2 text-xs text-ink-soft" data-testid="rate-bands-new-hint">
              A new band&rsquo;s token follows its name; adjust it before saving if you like.
              After the first save it locks (saved rates and billed entries carry it).
            </p>
          )}

          {problem !== null && (
            <p className="mt-3 text-xs text-pepper" data-testid="rate-bands-problem">
              {problem}
            </p>
          )}
          {saveError !== null && (
            <p role="alert" className="mt-3 text-xs text-pepper" data-testid="rate-bands-error">
              {saveError}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <Button
              variant="primary"
              size="sm"
              data-testid="rate-bands-save"
              disabled={!dirty || problem !== null || saveMut.isPending}
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate(bands.map((b) => ({ value: b.value, label: b.label.trim() })))}
            >
              Save
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="rate-bands-discard"
                onClick={() => {
                  setDraft(null);
                  setSaveError(null);
                }}
              >
                Discard changes
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
